/**
 * OrdersTab — multi-broker order blotter.
 *
 * Lists open + recent orders aggregated across every configured broker
 * (each row carries its broker tag from the backend) with one-click
 * cancel routed through the right adapter via ?broker=. Auto-refreshes
 * every 5s while visible.
 */
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { readBlotter } from '@/lib/apiErrors';
import { isVaultUnlocked, redirectToVault } from '@/lib/vault';
import { GRN, MUT, FONT } from '@/theme';

const RED = '#e05252';

interface Row {
  brokerOrderId: string;
  clientOrderId?: string;
  symbol: string;
  qty: number;
  side: string;
  orderType: string;
  tif?: string;
  limitPrice?: number | null;
  stopPrice?: number | null;
  status: string;
  filledQty?: number;
  avgFillPrice?: number | null;
  submittedAt?: number | string | null;
  broker?: string;
}

const OPEN_STATES = new Set(['new', 'accepted', 'pending_new', 'partially_filled', 'open', 'working', 'submitted']);

export function OrdersTab() {
  // null = NEVER READ. Distinct from [] ("read, and there are none"): an
  // initial [] rendered "OPEN ORDERS 0 / No working orders." under a failed
  // read -- an empty blotter asserting there is nothing working.
  const [rows, setRows] = useState<Row[] | null>(null);
  const [degraded, setDegraded] = useState<Array<{ broker: string; error: string }>>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const vaultOk = isVaultUnlocked();

  const load = useCallback(async () => {
    try {
      // A plain array, or { rows, degraded, partial } when a broker could not
      // be read (router blotterBody). The partial shape used to be treated
      // as the array and the missing broker vanished from the blotter.
      const b = readBlotter<Row>(await api<unknown>('/api/orders'));
      setRows(b.rows); setDegraded(b.degraded); setErr('');
    }
    catch (e) { setErr((e as Error).message); }
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const cancel = async (r: Row) => {
    if (!vaultOk) { toast.error('Unlock your broker vault to cancel orders.'); return; }
    // REFUSE rather than guess the venue. Defaulting an unknown broker to
    // 'alpaca' sends the cancel to the WRONG venue: it can report success while
    // the real order rests untouched at the venue that actually holds it.
    if (!r.broker) {
      toast.error('This order has no broker recorded — refusing to cancel, because guessing the venue could leave the real order live. Cancel it in the broker UI.');
      return;
    }
    setBusy(r.brokerOrderId);
    try {
      await api(`/api/orders/${encodeURIComponent(r.brokerOrderId)}?broker=${r.broker}`, { method: 'DELETE' });
      toast.success(`Cancel sent — ${r.symbol}`);
      await load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(null); }
  };

  const cell: React.CSSProperties = { fontFamily: 'Consolas, monospace', fontSize: 11, padding: '3px 10px', whiteSpace: 'nowrap' };
  const open = (rows ?? []).filter(r => OPEN_STATES.has((r.status || '').toLowerCase()));
  const done = (rows ?? []).filter(r => !OPEN_STATES.has((r.status || '').toLowerCase())).slice(0, 30);

  const table = (list: Row[], showCancel: boolean) => (
    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr style={{ color: MUT, fontSize: 9, letterSpacing: 1, textAlign: 'left' }}>
          <th style={cell}>SYMBOL</th><th style={cell}>SIDE</th><th style={cell}>QTY</th>
          <th style={cell}>TYPE</th><th style={cell}>PX</th><th style={cell}>STATUS</th>
          <th style={cell}>FILLED</th><th style={cell}>BROKER</th>{showCancel && <th style={cell}></th>}
        </tr>
      </thead>
      <tbody>
        {list.map(r => (
          <tr key={r.broker + r.brokerOrderId} style={{ borderBottom: '1px solid #222' }}>
            <td style={{ ...cell, fontWeight: 700 }}>{r.symbol}</td>
            <td style={{ ...cell, color: r.side === 'buy' ? GRN : RED }}>{(r.side || '').toUpperCase()}</td>
            <td style={cell}>{r.qty}</td>
            <td style={cell}>{r.orderType}{r.limitPrice ? ` @ ${r.limitPrice}` : ''}{r.stopPrice ? ` stp ${r.stopPrice}` : ''}</td>
            <td style={cell}>{r.avgFillPrice ?? '—'}</td>
            <td style={{ ...cell, color: MUT }}>{r.status}</td>
            {/* An UNKNOWN fill quantity is not zero. "0/100" reads as "nothing
                filled", which invites a re-fire of an order that may be done. */}
            <td style={cell}>{r.filledQty ?? '—'}/{r.qty}</td>
            {/* Never DISPLAY an unknown broker as alpaca -- the cancel below
                routes on this same value, so a guess here cancels at the wrong
                venue (or reports success against an order that still rests). */}
            <td style={{ ...cell, color: r.broker ? MUT : RED }}>{r.broker ?? 'UNKNOWN'}</td>
            {showCancel && (
              <td style={cell}>
                <button
                  disabled={busy === r.brokerOrderId || !vaultOk}
                  onClick={() => cancel(r)}
                  title={vaultOk ? undefined : 'Unlock your broker vault to cancel'}
                  style={{
                    background: 'transparent', color: RED, border: `1px solid ${RED}44`,
                    fontFamily: FONT, fontSize: 9, padding: '2px 8px', borderRadius: 2,
                    cursor: vaultOk ? 'pointer' : 'not-allowed', opacity: vaultOk ? 1 : 0.4,
                  }}
                >{busy === r.brokerOrderId ? '…' : 'CANCEL'}</button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div style={{ fontFamily: FONT, fontSize: 11 }}>
      {!vaultOk && (
        <div style={{ color: '#c08a3e', marginBottom: 6, fontSize: 10 }}>
          Broker vault locked — <span onClick={redirectToVault} style={{ color: '#d4a45a', cursor: 'pointer', textDecoration: 'underline' }}>unlock</span> to cancel orders.
        </div>
      )}
      {err && rows === null && (
        <div style={{ color: RED, marginBottom: 6 }}>
          ORDERS UNAVAILABLE — {err} (this is NOT "no orders")
        </div>
      )}
      {err && rows !== null && (
        <div style={{ color: RED, marginBottom: 6 }}>
          STALE — the last refresh failed: {err}. The rows below are from the previous read.
        </div>
      )}
      {degraded.length > 0 && (
        <div style={{ color: '#c08a3e', marginBottom: 6 }}>
          PARTIAL — could not read {degraded.map(d => `${d.broker} (${d.error})`).join(', ')}. Orders at
          {degraded.length > 1 ? ' those brokers' : ' that broker'} are NOT shown below.
        </div>
      )}
      {rows === null ? null : (<>
        <div style={{ color: MUT, fontSize: 9, letterSpacing: 1.5, margin: '2px 0 6px' }}>OPEN ORDERS · {open.length}{degraded.length ? ' (partial)' : ''}</div>
        {open.length ? table(open, true) : <div style={{ color: MUT }}>No working orders{degraded.length ? ' at the brokers that answered' : ''}.</div>}
        <div style={{ color: MUT, fontSize: 9, letterSpacing: 1.5, margin: '14px 0 6px' }}>RECENT · {done.length}{degraded.length ? ' (partial)' : ''}</div>
        {done.length ? table(done, false) : <div style={{ color: MUT }}>No recent orders{degraded.length ? ' at the brokers that answered' : ''}.</div>}
      </>)}
    </div>
  );
}
